import { useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useRoute, navigate } from "../hooks/useRoute";
import { maskPhone } from "../services/format";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { HoldingsTab } from "./HoldingsTab";
import { StocksTab } from "./StocksTab";
import { TasksTab } from "./TasksTab";

type Tab = "holdings" | "stocks" | "tasks";

const TAB_ROUTES: Record<Tab, string> = {
  holdings: "/holding",
  // 股票页默认加载多空信号视图及其筛选参数
  stocks: "/stocks?tab=dk&trend=duo&scope=all&indicator=MACD",
  tasks: "/tasks",
};

// 从当前路径解析激活 Tab
function tabFromRoute(route: string): Tab {
  if (route.startsWith("/stocks")) return "stocks";
  if (route.startsWith("/tasks")) return "tasks";
  return "holdings";
}

// 主题图标（SVG，跟随按钮文字颜色）
function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

// 首页：顶部导航 + 持仓/股票/任务 Tab（Tab 状态跟随 URL，刷新保持当前页面）
export function HomePage() {
  const route = useRoute();
  const { theme, toggleTheme } = useTheme();
  const { phone, logout } = useAuth();
  const tab = tabFromRoute(route);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const doLogout = async () => {
    setConfirmLogout(false);
    setMenuOpen(false);
    await logout();
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "holdings", label: "持仓" },
    { key: "stocks", label: "股票" },
    { key: "tasks", label: "任务" },
  ];

  return (
    <div className="max-w-2xl mx-auto min-h-screen" style={{ padding: "0 12px 40px" }}>
      <header
        className="sticky top-0 z-20 flex items-center justify-between py-3"
        style={{ background: "var(--bg)" }}
      >
        <div className="flex gap-5">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`tab ${tab === t.key ? "tab-active" : ""}`}
              onClick={() => navigate(TAB_ROUTES[t.key])}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            className="btn btn-sm"
            style={{ padding: "6px 8px" }}
            title={theme === "light" ? "切换到暗色主题" : "切换到亮色主题"}
            onClick={toggleTheme}
          >
            {theme === "light" ? <SunIcon /> : <MoonIcon />}
          </button>
          <div className="relative" ref={menuRef}>
            <button className="btn btn-sm" onClick={() => setMenuOpen((o) => !o)}>
              {maskPhone(phone ?? "")} ▾
            </button>
            {menuOpen && (
              <div
                className="card"
                style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, zIndex: 30, minWidth: 132, padding: 4 }}
              >
                <button
                  className="btn btn-sm w-full justify-start"
                  onClick={() => setConfirmLogout(true)}
                >
                  登出
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main>
        {tab === "holdings" && <HoldingsTab />}
        {tab === "stocks" && <StocksTab />}
        {tab === "tasks" && <TasksTab />}
      </main>

      <ConfirmDialog
        open={confirmLogout}
        title="确认登出"
        message="确定要退出当前账号吗？"
        confirmText="登出"
        danger
        onConfirm={doLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
